import React, { useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import './About.css';
import blogPosts from '../../data/blogPosts';
import { BlogRow, useSharedBlogPhotoSize } from '../Blog/BlogList';
import { VENUE_LISTING_URL } from '../Header/constants';
import HeroWave from '../HeroWave/HeroWave';

const ABOUT_HERO_TITLE_WORDS = [
  'For',
  'people,',
  'places,',
  'and',
  'the',
  'moments',
  'that',
  'bring',
  'them',
  'together',
];
const ABOUT_HERO_WORD_START = 0.12;
const ABOUT_HERO_WORD_STAGGER = 0.1;

const CLOSING_GRID_GAP = 22;
const CLOSING_GRID_RADIUS = 3.2;
const CLOSING_GRID_BURST_MS = 200;
const CLOSING_GRID_HOLD = 1.05;
const CLOSING_GRID_RING = [
  { max: 0.85, color: '#046242' },
  { max: 1.85, color: '#BED347' },
  { max: CLOSING_GRID_RADIUS, color: '#E4FD58' },
];

const AboutHowPlaceRow = ({ title, lead, primaryHref, primaryTo, primaryLabel, secondaryTo, variant }) => {
  const howPlaceRef = useRef(null);

  useEffect(() => {
    const node = howPlaceRef.current;
    if (!node) return undefined;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      node.classList.add('is-in');
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        node.classList.add('is-in');
        observer.disconnect();
      },
      { threshold: 0.4 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      className={`mb-how-place${variant ? ` about-how-place--${variant}` : ''}`}
      ref={howPlaceRef}
    >
      <div className="mb-how-place-rings" aria-hidden>
        <span className="mb-how-place-ring" />
        <span className="mb-how-place-ring" />
        <span className="mb-how-place-ring" />
        <span className="mb-how-place-ring" />
        <span className="mb-how-place-ring" />
      </div>
      <div className="mb-how-place-copy">
        <h2 className="mb-how-place-title">{title}</h2>
        <p className="about-how-place-lead">{lead}</p>
        <div className="about-how-place-actions">
          {primaryTo ? (
            <Link to={primaryTo} className="about-how-place-cta">
              {primaryLabel}
            </Link>
          ) : (
            <a
              href={primaryHref}
              target="_blank"
              rel="noopener noreferrer"
              className="about-how-place-cta"
            >
              {primaryLabel}
            </a>
          )}
          <Link to={secondaryTo} className="about-how-place-cta-secondary">
            Learn more
          </Link>
        </div>
      </div>
    </section>
  );
};

const AboutClosingPlace = () => {
  const gridRef = useRef(null);

  useEffect(() => {
    const canvas = gridRef.current;
    const wrap = canvas?.parentElement;
    if (!canvas || !wrap) return undefined;

    const ctx = canvas.getContext('2d');
    if (!ctx) return undefined;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const desktop = window.matchMedia('(min-width: 768px)');
    const clusters = [];
    let cols = 0;
    let rows = 0;
    let frameId = 0;
    let lastBurst = 0;

    const spawnBurst = () => {
      const count = 3 + Math.floor(Math.random() * 2);
      const margin = Math.ceil(CLOSING_GRID_RADIUS) + 1;
      for (let i = 0; i < count; i += 1) {
        clusters.push({
          col: margin + Math.floor(Math.random() * Math.max(1, cols - margin * 2)),
          row: margin + Math.floor(Math.random() * Math.max(1, rows - margin * 2)),
          born: performance.now(),
        });
      }
    };

    const ringColor = (distance) => {
      const ring = CLOSING_GRID_RING.find((item) => distance <= item.max);
      return ring?.color ?? '#E4FD58';
    };

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const width = wrap.clientWidth;
      const height = wrap.clientHeight;
      canvas.width = Math.max(1, Math.floor(width * dpr));
      canvas.height = Math.max(1, Math.floor(height * dpr));
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(width / CLOSING_GRID_GAP);
      rows = Math.ceil(height / CLOSING_GRID_GAP);
      clusters.length = 0;
      lastBurst = 0;
    };

    const draw = (now) => {
      if (!desktop.matches) {
        frameId = 0;
        return;
      }

      const width = wrap.clientWidth;
      const height = wrap.clientHeight;
      ctx.clearRect(0, 0, width, height);

      if (!reduced && now - lastBurst >= CLOSING_GRID_BURST_MS) {
        spawnBurst();
        lastBurst = now;
      }

      const hold = CLOSING_GRID_HOLD * 1000;
      for (let i = clusters.length - 1; i >= 0; i -= 1) {
        if (now - clusters[i].born >= hold) clusters.splice(i, 1);
      }

      const lit = new Map();
      clusters.forEach((cluster) => {
        const intensity = Math.sin(((now - cluster.born) / hold) * Math.PI);
        const extent = Math.ceil(CLOSING_GRID_RADIUS);
        for (let dy = -extent; dy <= extent; dy += 1) {
          for (let dx = -extent; dx <= extent; dx += 1) {
            const distance = Math.hypot(dx, dy);
            if (distance > CLOSING_GRID_RADIUS) continue;
            const key = `${cluster.col + dx},${cluster.row + dy}`;
            const prev = lit.get(key);
            if (!prev || intensity > prev.intensity) {
              lit.set(key, { color: ringColor(distance), intensity });
            }
          }
        }
      });

      for (let row = 0; row < rows; row += 1) {
        for (let col = 0; col < cols; col += 1) {
          const litDot = lit.get(`${col},${row}`);
          ctx.beginPath();
          ctx.arc(col * CLOSING_GRID_GAP, row * CLOSING_GRID_GAP, 1.05, 0, Math.PI * 2);
          if (litDot) {
            ctx.globalAlpha = 0.08 + litDot.intensity * 0.92;
            ctx.fillStyle = litDot.color;
          } else {
            ctx.globalAlpha = 1;
            ctx.fillStyle = 'rgba(17, 17, 17, 0.07)';
          }
          ctx.fill();
        }
      }
      ctx.globalAlpha = 1;
      frameId = window.requestAnimationFrame(draw);
    };

    const start = () => {
      if (!desktop.matches || frameId) return;
      resize();
      frameId = window.requestAnimationFrame(draw);
    };

    const stop = () => {
      window.cancelAnimationFrame(frameId);
      frameId = 0;
    };

    const onBreakpoint = () => {
      if (desktop.matches) start();
      else stop();
    };

    resize();
    const observer = new ResizeObserver(() => {
      if (desktop.matches) resize();
    });
    observer.observe(wrap);
    desktop.addEventListener('change', onBreakpoint);
    onBreakpoint();
    return () => {
      desktop.removeEventListener('change', onBreakpoint);
      observer.disconnect();
      stop();
    };
  }, []);

  return (
    <section className="mb-how-place about-closing-place">
      <div className="about-closing-place-grid" aria-hidden>
        <canvas ref={gridRef} className="about-closing-place-grid-dots" />
      </div>
      <div className="mb-how-place-copy">
        <h2 className="mb-how-place-title">
          And if you simply believe that small, local, human moments matter…
        </h2>
        <p className="about-how-place-lead">You&apos;re already part of what we&apos;re building.</p>
      </div>
    </section>
  );
};

const FOUNDER_QUOTE_LINES = [
  'Where people return.',
  'Where regulars form.',
  'Where community grows.',
];

const About = () => {
  const founderRef = useRef(null);
  const teamRef = useRef(null);
  const howPlacePairRef = useRef(null);
  const journalPostsRef = useRef(null);
  const logosRef = useRef(null);
  const contentRef = useRef(null);
  useSharedBlogPhotoSize(journalPostsRef);
  const featuredPosts = useMemo(() => {
    if (!blogPosts || blogPosts.length === 0) return [];
    const shuffled = [...blogPosts].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 2);
  }, []);

  useEffect(() => {
    const node = founderRef.current;
    if (!node) return undefined;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      node.classList.add('is-in');
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        node.classList.add('is-in');
        observer.disconnect();
      },
      { threshold: 0.4 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const root = teamRef.current;
    const copy = root?.querySelector('.about-team-copy');
    if (!root || !copy) return undefined;

    const applySize = () => {
      const size = Math.round(copy.getBoundingClientRect().height);
      if (size > 0) root.style.setProperty('--about-team-photo-size', `${size}px`);
    };

    applySize();
    const observer = new ResizeObserver(applySize);
    observer.observe(copy);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const root = howPlacePairRef.current;
    if (!root) return undefined;

    const copies = () => [...root.querySelectorAll('.mb-how-place-copy')];

    const applySize = () => {
      const copyHeights = copies().map((node) => node.getBoundingClientRect().height);
      const maxCopy = Math.round(Math.max(0, ...copyHeights));
      if (maxCopy > 0) {
        root.style.setProperty('--about-how-place-copy-height', `${maxCopy}px`);
      }

      const cards = [...root.querySelectorAll(':scope > .mb-how-place')];
      const cardHeight = Math.round(
        Math.max(0, ...cards.map((node) => node.getBoundingClientRect().height))
      );
      if (cardHeight > 0) {
        root.closest('.about-content')?.style.setProperty(
          '--about-how-place-card-height',
          `${cardHeight}px`
        );
      }
    };

    applySize();
    const observer = new ResizeObserver(applySize);
    copies().forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const logos = logosRef.current;
    const content = contentRef.current;
    if (!logos || !content) return undefined;

    const applyWidth = () => {
      const items = [...logos.children];
      if (items.length === 0) return;
      const rects = items.map((node) => node.getBoundingClientRect());
      const left = Math.min(...rects.map((rect) => rect.left));
      const right = Math.max(...rects.map((rect) => rect.right));
      const width = Math.round(right - left);
      if (width > 0) {
        content.style.setProperty('--about-logos-width', `${width}px`);
      }
    };

    applyWidth();
    const observer = new ResizeObserver(applyWidth);
    observer.observe(logos);
    const images = [...logos.querySelectorAll('img')];
    images.forEach((img) => {
      if (!img.complete) img.addEventListener('load', applyWidth);
    });
    window.addEventListener('resize', applyWidth);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', applyWidth);
      images.forEach((img) => img.removeEventListener('load', applyWidth));
    };
  }, []);

  return (
    <div className="about-content" ref={contentRef}>
      <section className="about-hero">
        <div className="brand-wash" aria-hidden>
          <div className="l2-footer-glow brand-wash--flip" />
          <div className="brand-wash-fade" />
        </div>
        <div className="about-hero-inner">
          <h1 className="about-hero-title">
            {ABOUT_HERO_TITLE_WORDS.map((word, wordIndex) => (
              <span
                key={`${word}-${wordIndex}`}
                className="about-hero-word"
                style={{
                  animationDelay: `${ABOUT_HERO_WORD_START + wordIndex * ABOUT_HERO_WORD_STAGGER}s`,
                }}
              >
                {word}
              </span>
            ))}
          </h1>
          <p className="about-hero-subtitle">
            Occasia exists to help people gather, form real connections,
            <br />
            and keep local spaces alive.
          </p>
        </div>
        <HeroWave />
      </section>
      <div className="about-sponsors">
        <p className="about-sponsors-label">Backed by the best</p>
        <div className="about-sponsors-logos" ref={logosRef}>
          <div className="about-sponsor-item">
            <img src="/Global%20Shapers%20Logo%201.png" alt="Global Shapers" />
          </div>
          <div className="about-sponsor-item">
            <img src="/Girldreamer%20Logo%201.png" alt="Girldreamer" />
          </div>
          <div className="about-sponsor-item">
            <img src="/bae.png" alt="BAE" />
          </div>
          <div className="about-sponsor-item">
            <img src="/hatch.png" alt="Hatch" />
          </div>
          <div className="about-sponsor-item about-sponsor-item--badge">
            <img src="/lse-generate-fellow.png" alt="LSE Generate Entrepreneurship Fellow" />
          </div>
          <div className="about-sponsor-item">
            <img src="/do-it-now.png" alt="Do it Now" />
          </div>
        </div>
      </div>

      <section className="about-beliefs">
        <div className="about-beliefs-header">
          <h2 className="about-beliefs-title">
            <span className="about-beliefs-title-line">Three simple beliefs that guide</span>
            <span className="about-beliefs-title-line about-beliefs-title-line--hl">everything we build</span>
          </h2>
        </div>
        <div className="about-beliefs-steps">
          <div className="about-belief">
            <img src="/about/purpose.png" alt="" className="about-belief-icon" aria-hidden />
            <div className="about-belief-rule" aria-hidden />
            <div className="about-belief-copy">
              <h3 className="about-belief-heading">Our Purpose</h3>
              <p className="about-belief-text">
                Keep local third spaces alive.
                <br />
                Because when small venues thrive, communities do too. Every café, bar, and
                studio that stays open is a win for the neighbourhood.
              </p>
            </div>
          </div>
          <div className="about-belief">
            <img src="/about/mission.png" alt="" className="about-belief-icon" aria-hidden />
            <div className="about-belief-rule" aria-hidden />
            <div className="about-belief-copy">
              <h3 className="about-belief-heading">Our Mission</h3>
              <p className="about-belief-text">
                Make hosting simple and sustainable.
                <br />
                We're removing the friction between people who want to host small events
                and the spaces that could welcome them.
              </p>
            </div>
          </div>
          <div className="about-belief">
            <img src="/about/vision.png" alt="" className="about-belief-icon" aria-hidden />
            <div className="about-belief-rule" aria-hidden />
            <div className="about-belief-copy">
              <h3 className="about-belief-heading">Our Vision</h3>
              <p className="about-belief-text">
                Vibrant spaces in every neighbourhood.
                <br />
                A world where local venues are thriving hubs of connection, creativity, and
                community opportunity.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="about-founder" ref={founderRef}>
        <div className="about-founder-copy">
          <p className="about-founder-kicker">WORDS FROM OUR FOUNDER</p>
          <h2 className="about-founder-quote">
            <span className="about-founder-quote-lead">
              Occasia helps spaces
              <br />
              become places
            </span>
            <span className="about-founder-quote-lines">
              {FOUNDER_QUOTE_LINES.map((line, index) => (
                <span
                  key={line}
                  className="about-founder-quote-line"
                  style={{ animationDelay: `${0.08 + index * 0.16}s` }}
                >
                  {line}
                </span>
              ))}
            </span>
          </h2>
        </div>
        <div className="about-founder-image">
          <img src="/Girldreamer_photo.png" alt="Priyanka Roychoudhury, Founder" />
        </div>
      </section>

      <section className="about-team" ref={teamRef}>
        <h2 className="about-team-title">
          <span className="about-team-title-line">The team</span>
          <span className="about-team-title-line about-team-title-line--hl">building Occasia</span>
        </h2>
        <div className="about-team-members">
          <article className="about-team-member">
            <div className="about-team-photo">
              <img src="/pori.png" alt="Priyanka Roychoudhury" />
            </div>
            <div className="about-team-copy">
              <h3 className="about-team-name">Priyanka Roychoudhury</h3>
              <p className="about-team-role">CEO &amp; Founder</p>
              <p className="about-team-bio">
                Priyanka brings together 6+ years in consulting and 10+ years in community
                building, with a strong foundation in data, strategy, and problem-solving.
              </p>
            </div>
          </article>
          <article className="about-team-member">
            <div className="about-team-photo">
              <img src="/dugi.png" alt="Hrishikesh Sarma" />
            </div>
            <div className="about-team-copy">
              <h3 className="about-team-name">Hrishikesh Sarma</h3>
              <p className="about-team-role">Chief Technical Officer</p>
              <p className="about-team-bio">
                Hrishikesh has 8+ years of experience building scalable data platforms and
                analytics systems across real-time and batch pipelines.
              </p>
            </div>
          </article>
          <article className="about-team-member">
            <div className="about-team-photo">
              <img src="/sunny.png" alt="Ritwij Goswami" />
            </div>
            <div className="about-team-copy">
              <h3 className="about-team-name">Ritwij Goswami</h3>
              <p className="about-team-role">Chief Product Officer</p>
              <p className="about-team-bio">
                Ritwij brings 8+ years of experience across B2B and B2C, leading
                end-to-end product design from concept to launch.
              </p>
            </div>
          </article>
        </div>
      </section>

      <div className="about-how-place-pair" ref={howPlacePairRef}>
        <AboutHowPlaceRow
          title="If you run a space,"
          lead={
            <>
              We&apos;d love to help it
              <br />
              stay alive.
            </>
          }
          primaryHref={VENUE_LISTING_URL}
          primaryLabel="List your venue"
          secondaryTo="/onboard?persona=venues"
        />
        <AboutHowPlaceRow
          title="If you host events,"
          lead={
            <>
              We&apos;d love to help you
              <br />
              find your place.
            </>
          }
          primaryTo="/?brief=1"
          primaryLabel="Find a venue"
          secondaryTo="/"
          variant="warm"
        />
      </div>

      <AboutClosingPlace />

      {/* ===== Blog Section ===== */}
      <section className="about-blog">
        <div className="about-blog-heading">
          <h2 className="about-blog-title">From the Occasia journal</h2>
          <p className="about-blog-subtitle">Stories, tips, and things we're learning</p>
        </div>
        <Link to="/blog" className="about-blog-explore-btn">Explore our journal</Link>
        <div className="about-blog-divider" aria-hidden />
        <div className="about-blog-posts" ref={journalPostsRef}>
          {featuredPosts.map((post) => (
            <BlogRow post={post} key={post.slug} />
          ))}
        </div>
      </section>
    </div>
  );
};

export default About;
